import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';

var API_URL = process.env.REACT_APP_BACKEND_URL;

function ReviewsCarousel() {
  var { t } = useTranslation();
  var [reviews, setReviews] = useState([]);
  var [currentIndex, setCurrentIndex] = useState(0);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    fetch(API_URL + '/api/reviews/featured/all')
      .then(function(res) { return res.json(); })
      .then(function(data) {
        setReviews(data.reviews || []);
        setLoading(false);
      })
      .catch(function(err) {
        console.error('Failed to fetch reviews:', err);
        setLoading(false);
      });
  }, []);

  function nextReview() {
    setCurrentIndex(function(prev) {
      return prev === reviews.length - 1 ? 0 : prev + 1;
    });
  }

  function prevReview() {
    setCurrentIndex(function(prev) {
      return prev === 0 ? reviews.length - 1 : prev - 1;
    });
  }

  function renderStars(rating) {
    var stars = [];
    for (var i = 0; i < 5; i++) {
      stars.push(
        <Star
          key={i}
          className={'w-5 h-5 ' + (i < rating ? 'fill-[#d4af37] text-[#d4af37]' : 'text-neutral-600')}
        />
      );
    }
    return stars;
  }

  // Don't render if no reviews
  if (loading || reviews.length === 0) {
    return null;
  }

  var review = reviews[currentIndex];

  return (
    <section data-testid="reviews-carousel" className="py-20 bg-neutral-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <p className="text-sm tracking-widest uppercase text-[#d4af37] mb-3">{t('customerReviews')}</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white">{t('whatCustomersSay')}</h2>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {/* Navigation buttons */}
          {reviews.length > 1 && (
            <>
              <button
                onClick={prevReview}
                data-testid="review-prev-btn"
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextReview}
                data-testid="review-next-btn"
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Review card */}
          <div className="bg-neutral-900/50 border border-white/5 rounded-2xl p-8 md:p-12 relative">
            <Quote className="w-16 h-16 text-[#d4af37]/10 absolute top-6 right-6" />
            
            <div className="flex items-center gap-1 mb-6">
              {renderStars(review.rating)}
            </div>
            
            <p className="text-xl md:text-2xl text-white leading-relaxed mb-8 italic">
              "{review.text}"
            </p>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-semibold">{review.name}</p>
                <div className="flex items-center gap-2 text-sm text-neutral-500">
                  {review.productSlug && (
                    <Link 
                      to={'/product/' + review.productSlug}
                      className="text-[#d4af37] hover:underline"
                    >
                      {t('verifiedPurchase')}
                    </Link>
                  )}
                  {review.date && <span>• {review.date}</span>}
                </div>
              </div>
              
              {/* Dots indicator */}
              {reviews.length > 1 && (
                <div className="flex gap-2">
                  {reviews.map(function(_, idx) {
                    return (
                      <button
                        key={idx}
                        onClick={function() { setCurrentIndex(idx); }}
                        className={'w-2 h-2 rounded-full transition-colors ' + 
                          (idx === currentIndex ? 'bg-[#d4af37]' : 'bg-neutral-700')}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ReviewsCarousel;
